import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { AutomationFlowStatus, FlowDefinition } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAutomationFlow, useUpdateMyAutomationFlow } from '../../api/automation-flows';
import { LazyFlowDefinitionEditor } from './builder/LazyFlowDefinitionEditor';
import { flowProblems } from './builder/flow-graph';

const STATUS_OPTIONS: { value: AutomationFlowStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active (this is what customers experience)' },
  { value: 'ARCHIVED', label: 'Archived (behavior reverts to the platform default)' },
];

/**
 * Editing a tenant's own cloned conversation flow (docs/API_SPEC.md section
 * 22) on the visual drag-and-drop canvas - same editor as the platform-admin
 * form, minus `key` (a clone always keeps the key of the system flow it was
 * cloned from, so the same "Start a flow" action keeps finding it). Cloning
 * itself happens from `AutomationFlowsPage.tsx`; this page only ever edits a
 * clone this tenant already owns (the server 404s on anything else).
 */
export function AutomationFlowFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const existing = useAutomationFlow(id);
  const update = useUpdateMyAutomationFlow(id ?? '');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<AutomationFlowStatus>('ACTIVE');
  const [definition, setDefinition] = useState<FlowDefinition | null>(null);
  const [jsonValid, setJsonValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existing.data) {
      const f = existing.data;
      setName(f.name);
      setDescription(f.description ?? '');
      setStatus(f.status);
      setDefinition(f.definition);
    }
  }, [existing.data]);

  if (!id) return <Navigate to="/automation-flows" replace />;
  if (existing.isError) return <PageState kind="error" message={existing.error.message} />;
  if (existing.isPending || !definition) {
    return <PageState kind="loading" message="Loading your flow…" />;
  }
  if (existing.data.isSystem) return <Navigate to="/automation-flows" replace />;

  function clearFieldError(k: string) {
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const trimmedName = name.trim();
    if (trimmedName.length < 1) errors.name = 'Enter a name.';
    else if (trimmedName.length > 200) errors.name = 'Name must be 200 characters or fewer.';
    if (description.trim().length > 2000) {
      errors.description = 'Description must be 2000 characters or fewer.';
    }
    const problems = flowProblems(jsonValid ? definition : null);
    if (problems.length) errors.definition = problems.join(' ');
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0 || !definition) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    try {
      await update.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        status,
        definition,
      });
      toast.success('Your flow was updated.');
      navigate('/automation-flows', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          const mapped: Record<string, string> = {};
          for (const [k, message] of Object.entries(serverFieldErrors)) {
            mapped[k.startsWith('definition') ? 'definition' : k] = message;
          }
          setFieldErrors(mapped);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save your flow.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Edit your flow</h1>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Your customized copy of <strong>{existing.data.name}</strong>. It runs instead of the
        platform default while it's Active; archive or delete it to go back to the default.
      </p>
      <Card>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={fieldErrors.description}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as AutomationFlowStatus)}
            options={STATUS_OPTIONS}
          />
        </div>
      </Card>

      <LazyFlowDefinitionEditor
        value={definition}
        onChange={(next) => {
          setDefinition(next);
          clearFieldError('definition');
        }}
        onJsonValidityChange={setJsonValid}
        error={fieldErrors.definition}
      />

      {error ? (
        <p className="ne-auth__error" role="alert">
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate('/automation-flows')}>
          Cancel
        </Button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automation-flows">← All flows</Link>
      </p>
    </form>
  );
}
